import type { ReactNode } from "react";

/**
 * Notification categories. Mirrors the backend `NotificationCategory` enum
 * (values are the same strings the API sends/expects).
 */
export enum NotificationCategory {
  TRANSACTIONS = "transactions",
  SERVICES = "services",
  ACTIVITIES = "activities",
  PROMOTIONS = "promotions",
}

/**
 * Every notification type the backend may send. The UI must render unknown
 * types gracefully (generic fallback) — more will be added server-side without
 * a matching frontend release.
 */
export type NotificationType =
  | "transaction_received"
  | "settlement_completed"
  | "settlement_failed"
  | "login"
  | "kyc_verified"
  | "kyc_rejected"
  | "bank_account_added"
  | "bank_account_default_set"
  | "wallet_address_created"
  | "security_alert"
  | "system_announcement"
  | "broadcast";

/**
 * Types whose `reference_id` points at a transaction and should deep-link to
 * the receipt page at `/transactions/{reference_id}`. Every other type has no
 * established route and is handled in place.
 */
export const TRANSACTION_LINK_TYPES: NotificationType[] = [
  "transaction_received",
  "settlement_completed",
  "settlement_failed",
];

/** Generic response envelope used by every notification REST endpoint. */
export interface ApiEnvelope<T> {
  success: boolean;
  message: string;
  data: T;
}

/** Raw notification as returned by the list endpoint / SSE "created" events. */
export interface NotificationApi {
  id: string;
  user_id: string;
  category: NotificationCategory;
  type: NotificationType;
  title: string;
  message: string;
  /** Meaning depends on `type`. Only usable by the frontend for transaction types. */
  reference_id: string | null;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
  updated_at: string | null;
}

export interface NotificationListParams {
  category?: NotificationCategory;
  page?: number;
  limit?: number;
}

export interface NotificationListMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface NotificationListData {
  items: NotificationApi[];
  meta: NotificationListMeta;
}

export interface UnreadCountData {
  transactions: number;
  services: number;
  activities: number;
  promotions: number;
  total: number;
}

export interface StreamTicketData {
  ticket: string;
  expires_in_seconds: number;
}

export interface MarkAllReadData {
  count: number;
}

/** Unnamed SSE events carried in `EventSource` `message` events. */
export type NotificationStreamEvent =
  | { event: "created"; notification: NotificationApi }
  | { event: "read"; notification_id: string }
  | { event: "read_all"; category: NotificationCategory | null };

// ─────────────────────────────────────────────────────────────────────
// UI view model — produced by `mapNotification` from a `NotificationApi`.
// ─────────────────────────────────────────────────────────────────────

/** Visual bucket driving the icon and accent colour of a notification row. */
export type NotificationVisualType = "success" | "info" | "warning" | "error";

export interface AppNotification {
  id: string;
  category: NotificationCategory;
  /** The original backend type — kept for deep-link decisions and future rendering. */
  notificationType: NotificationType;
  /** Visual bucket derived from `notificationType`. */
  type: NotificationVisualType;
  icon?: ReactNode;
  title: string;
  message: string;
  timestamp: Date;
  isRead: boolean;
  referenceId: string | null;
  /**
   * Present only for transaction deep-link types. Used to navigate to
   * `/transactions/{transactionId}`.
   */
  transactionId?: string;
  /** Whether clicking the notification navigates somewhere. */
  hasDetails?: boolean;
}

export interface NotificationGroup {
  /** Human label for the day, e.g. "Today" or "17 December 2025". */
  date: string;
  notifications: AppNotification[];
}

/** Unread totals keyed by every category. */
export type UnreadCountByCategory = Record<NotificationCategory, number>;
